import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PlayerAction, RoomView } from "@grundbesitz/shared";
import { describeEvents, pickToasts, soundFor } from "../../lib/events";
import type { EventContext, Tone } from "../../lib/events";
import { nameOf } from "../../lib/gameView";
import { playSound } from "../../lib/sound";
import { secondsUntil, useStore } from "../../lib/store";
import { speak, stopSpeech } from "../../lib/voice";
import { voiceFor } from "../../lib/voiceLines";
import { DESKTOP_QUERY } from "./uiStore";

/** True on screens where the sidebar layout applies (>= 1024 px). */
export function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    onChange();
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return desktop;
}

/** Whole seconds left until a server timestamp, updated while it runs. Null when there is no timer. */
export function useCountdown(at: number | null | undefined): number | null {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!at) return;
    const id = setInterval(() => tick((n) => n + 1), 250);
    return () => clearInterval(id);
  }, [at]);
  return secondsUntil(at);
}

/** Sends game actions and reports when one is in flight, so a double click cannot send it twice. */
export function useAct() {
  const act = useStore((s) => s.act);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const run = useCallback(
    async (action: PlayerAction) => {
      if (running.current) return;
      running.current = true;
      setBusy(true);
      try {
        await act(action);
      } finally {
        running.current = false;
        setBusy(false);
      }
    },
    [act],
  );
  return { run, busy };
}

export function eventContext(room: RoomView | null): EventContext {
  return {
    selfId: room?.you.playerId ?? null,
    playerName: (id) => (room ? nameOf(room.game, room.members, id) : "Unbekannt"),
  };
}

export interface LogEntry {
  id: string;
  text: string;
  tone: Tone;
  involvesSelf: boolean;
}

const LOG_LIMIT = 150;

/** Log entries built from the event batches the store collected since the page was opened. */
export function useGameLog(room: RoomView): LogEntry[] {
  const batches = useStore((s) => s.eventBatches);
  const selfId = room.you.playerId;
  const members = room.members;
  const players = room.game?.players;
  return useMemo(() => {
    const ctx: EventContext = {
      selfId,
      playerName: (id) => players?.find((p) => p.id === id)?.name ?? members.find((m) => m.id === id)?.name ?? "Unbekannt",
    };
    const entries: LogEntry[] = [];
    for (const batch of batches) {
      describeEvents(batch.events, ctx).forEach((d, i) => entries.push({ id: `${batch.id}-${i}`, ...d }));
    }
    return entries.slice(-LOG_LIMIT);
  }, [batches, selfId, members, players]);
}

/** Toasts, one sound and the spoken lines per event batch. Mount once. */
export function useEventEffects(): void {
  const subscribe = useStore((s) => s.subscribeEvents);
  useEffect(() => {
    const unsubscribe = subscribe((batch) => {
      const { room, toast, soundOn } = useStore.getState();
      if (!room) return;
      const ctx = eventContext(room);
      for (const t of pickToasts(batch.events, ctx)) toast(t.text, t.kind);
      const sound = soundFor(batch.events, ctx);
      if (sound && soundOn) playSound(sound);
      speak(voiceFor(batch.events, ctx));
    });
    return () => {
      unsubscribe();
      stopSpeech();
    };
  }, [subscribe]);
}
